import { GetActiveFeatureFlagsGQL, GetActiveFeatureFlagsQuery } from '@/generated/graphql';
import {
  ENVIRONMENT_INITIALIZER,
  Injectable,
  Provider,
  signal,
  WritableSignal,
} from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { firstValueFrom } from 'rxjs';
import { FeatureFlagsEnum } from '@/generated/featureFlags.enum';
import { DebugableService } from '../debug/debug.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fluxLogger } from '../logger/logger.instance';

const logger = fluxLogger.extend('feature-flag');

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements DebugableService {
  public readonly featureFlags: WritableSignal<GetActiveFeatureFlagsQuery['activeFeatureFlags']> =
    signal([]);

  public readonly featureFlagMap: Map<
    FeatureFlagsEnum,
    GetActiveFeatureFlagsQuery['activeFeatureFlags'][0]
  > = new Map();

  private fetched = false;

  public constructor(
    public readonly getActiveFeatureFlags: GetActiveFeatureFlagsGQL,
    public readonly authService: AuthService,
  ) {
    this.authService.authenticatedUser$.pipe(takeUntilDestroyed()).subscribe(() => {
      void this.getFeatureFlags();
    });
  }

  public async getFeatureFlags() {
    const result = await firstValueFrom(this.getActiveFeatureFlags.fetch());

    if (!result.data?.activeFeatureFlags) {
      logger.error('No data returned from getActiveFeatureFlags');
      return;
    }

    this.featureFlags.set(result.data.activeFeatureFlags);
    this.featureFlagMap.clear();
    this.fetched = true;

    for (const featureFlag of result.data.activeFeatureFlags) {
      this.featureFlagMap.set(featureFlag.slug as FeatureFlagsEnum, featureFlag);
    }

    if (logger.enabled()) {
      logger.info('Feature flags fetched', [...this.featureFlags(), this.featureFlagMap]);
    }
  }

  public async isFeatureFlagEnabled(featureFlag: FeatureFlagsEnum): Promise<boolean> {
    if (!this.fetched) {
      await this.getFeatureFlags();
    }

    return this.featureFlagMap.get(featureFlag)?.active ?? false;
  }

  public static provider(): Provider {
    return {
      provide: ENVIRONMENT_INITIALIZER,
      useFactory: (featureFlagService: FeatureFlagService) => {
        logger.info('Initializing feature flags');
        return () => {
          featureFlagService.getFeatureFlags();
        };
      },
      multi: true,
      deps: [FeatureFlagService],
    };
  }

  public generateDebugReport() {
    return {
      featureFlags: this.featureFlags(),
      featureFlagMap: this.featureFlagMap,
    };
  }
}
